// Finance master data: the service catalogue, price lists, payers and the quote
// (docs/plans/phase1-finance-catalogue.md §3.3). Catalogue data is not patient data,
// so item searches may travel in the query string.
import { queryOptions } from '@tanstack/react-query';
import {
  pageSchema,
  payerDetailSchema,
  payerSchema,
  priceListAssignmentSchema,
  priceListSchema,
  priceSchema,
  quoteResponseSchema,
  schemeSchema,
  serviceItemCodeSchema,
  serviceItemDetailSchema,
  serviceItemRowSchema,
  serviceItemSchema,
  splitRuleSchema,
  type addItemCodeRequestSchema,
  type changePriceRequestSchema,
  type changeSplitRuleRequestSchema,
  type createPayerRequestSchema,
  type createPriceListRequestSchema,
  type createSchemeRequestSchema,
  type createServiceItemRequestSchema,
  type putPriceListAssignmentRequestSchema,
  type quoteRequestSchema,
  type serviceItemListQuerySchema,
  type setAvailabilityRequestSchema,
  type updateServiceItemRequestSchema,
} from '@wodi/shared';
import { z } from 'zod';

import { api } from './client.ts';

export type ItemListFilter = Omit<z.input<typeof serviceItemListQuerySchema>, 'limit'>;

function queryString(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params))
    if (value !== undefined && value !== '') search.set(key, String(value));
  const text = search.toString();
  return text === '' ? '' : `?${text}`;
}

export function listItems(filter: ItemListFilter, limit = 50) {
  return api('GET', `/catalogue/items${queryString({ ...filter, limit })}`, {
    schema: pageSchema(serviceItemRowSchema),
  });
}

export const itemQuery = (itemId: string) =>
  queryOptions({
    queryKey: ['catalogue', 'item', itemId],
    queryFn: () => api('GET', `/catalogue/items/${itemId}`, { schema: serviceItemDetailSchema }),
  });

export function createItem(body: z.input<typeof createServiceItemRequestSchema>) {
  return api('POST', '/catalogue/items', { body, schema: serviceItemDetailSchema });
}

export function updateItem(itemId: string, body: z.input<typeof updateServiceItemRequestSchema>) {
  return api('PATCH', `/catalogue/items/${itemId}`, { body, schema: serviceItemSchema });
}

export function setItemActive(
  itemId: string,
  active: boolean,
  body: { expectedVersion: number; reason?: string },
) {
  return api('POST', `/catalogue/items/${itemId}/${active ? 'reactivate' : 'deactivate'}`, {
    body,
    schema: serviceItemSchema,
  });
}

export function addItemCode(itemId: string, body: z.input<typeof addItemCodeRequestSchema>) {
  return api('POST', `/catalogue/items/${itemId}/codes`, { body, schema: serviceItemCodeSchema });
}

export function setAvailability(
  itemId: string,
  facilityId: string,
  body: z.input<typeof setAvailabilityRequestSchema>,
) {
  return api('PUT', `/catalogue/items/${itemId}/availability/${facilityId}`, {
    body,
    schema: serviceItemDetailSchema,
  });
}

export const priceListsQuery = queryOptions({
  queryKey: ['price-lists'],
  queryFn: () => api('GET', '/price-lists', { schema: z.array(priceListSchema) }),
});

export function createPriceList(body: z.input<typeof createPriceListRequestSchema>) {
  return api('POST', '/price-lists', { body, schema: priceListSchema });
}

export function changePrice(listId: string, body: z.input<typeof changePriceRequestSchema>) {
  return api('POST', `/price-lists/${listId}/prices`, { body, schema: priceSchema });
}

export function withdrawPrice(priceId: string, reason: string) {
  return api('POST', `/prices/${priceId}/entered-in-error`, {
    body: { reason },
    schema: priceSchema,
  });
}

export const priceHistoryQuery = (listId: string, itemId: string) =>
  queryOptions({
    queryKey: ['price-lists', listId, 'history', itemId],
    queryFn: () =>
      api(
        'GET',
        `/price-lists/${listId}/prices${queryString({ serviceItemId: itemId, includeHistory: true, limit: 100 })}`,
        { schema: pageSchema(priceSchema) },
      ),
  });

export const payersQuery = queryOptions({
  queryKey: ['payers'],
  queryFn: () => api('GET', '/payers', { schema: z.array(payerSchema) }),
});

export const payerQuery = (payerId: string) =>
  queryOptions({
    queryKey: ['payers', payerId],
    queryFn: () => api('GET', `/payers/${payerId}`, { schema: payerDetailSchema }),
  });

export function createPayer(body: z.input<typeof createPayerRequestSchema>) {
  return api('POST', '/payers', { body, schema: payerSchema });
}

export function createScheme(payerId: string, body: z.input<typeof createSchemeRequestSchema>) {
  return api('POST', `/payers/${payerId}/schemes`, { body, schema: schemeSchema });
}

export function reviewScheme(schemeId: string, expectedVersion: number) {
  return api('POST', `/schemes/${schemeId}/review`, {
    body: { expectedVersion },
    schema: schemeSchema,
  });
}

export const assignmentsQuery = (payerId: string) =>
  queryOptions({
    queryKey: ['price-list-assignments', payerId],
    queryFn: () =>
      api('GET', `/price-list-assignments?payerId=${payerId}`, {
        schema: z.array(priceListAssignmentSchema),
      }),
  });

export function putAssignment(body: z.input<typeof putPriceListAssignmentRequestSchema>) {
  return api('PUT', '/price-list-assignments', {
    body,
    schema: z.array(priceListAssignmentSchema),
  });
}

export const splitRulesQuery = (payerId: string) =>
  queryOptions({
    queryKey: ['payers', payerId, 'split-rules'],
    queryFn: () =>
      api('GET', `/payers/${payerId}/split-rules`, { schema: z.array(splitRuleSchema) }),
  });

export function changeSplitRule(
  payerId: string,
  body: z.input<typeof changeSplitRuleRequestSchema>,
) {
  return api('POST', `/payers/${payerId}/split-rules`, { body, schema: splitRuleSchema });
}

export function quote(body: z.input<typeof quoteRequestSchema>) {
  return api('POST', '/prices/quote', { body, schema: quoteResponseSchema });
}
